import {FilterFacetsService} from '../shared/filter-facets.service';
import {hasFilterValue, numericFilterCount, registrationFilterCount} from '../shared/filter-section-state';
import {Component, inject, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {CompanyService} from '../../../services/enrich/company.service';
import {BrregCompany} from '../../../models/company.model';
import {ClientService} from '../../../services/auth/client.service';
import {ClientLoadingService} from '../../../services/auth/client-loading.service';
import {HttpErrorResponse} from '@angular/common/http';
import {HubspotService} from '../../../services/enrich/hubspot.service';
import {
  EnrichSettings,
  HubspotCompany,
  HubspotImportedCompaniesTruncation
} from '../../../models/hubspot.model';

import {MaterialModule} from '../../../material.module';
import {MatTabChangeEvent} from '@angular/material/tabs';
import {ActivatedRoute, Router} from '@angular/router';
import {forkJoin, of} from 'rxjs';
import {catchError, finalize} from 'rxjs/operators';
import {EnrichConsoleDiagnosticsService} from '../../../services/enrich';
import {FilterMultiselectComponent, selectionValues} from '../shared/filter-multiselect.component';
import {ToastService} from '../../../services/layout/toast.service';
import {BreadcrumbsComponent} from '../../../shared/breadcrumbs/breadcrumbs.component';
import {EnrichSetupService} from '../../../services/enrich/enrich-setup.service';

@Component({
  selector: 'app-enrich-companies',
  templateUrl: './companies.component.html',
  styleUrls: ['./companies.component.scss'],
  host: {class: 'webinars-m3 webinars-shell'},
  standalone: true,
  providers: [FilterFacetsService],
    imports: [MaterialModule, ReactiveFormsModule, FilterMultiselectComponent, BreadcrumbsComponent]
})
export class EnrichCompaniesComponent implements OnInit {
    private fb = inject(FormBuilder);
    private companyService = inject(CompanyService);
    private hubspotService = inject(HubspotService);
    private clientService = inject(ClientService);
    private clientLoadingService = inject(ClientLoadingService);
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private toastService = inject(ToastService);
    private enrichDiagnostics = inject(EnrichConsoleDiagnosticsService);
    private enrichSetupService = inject(EnrichSetupService);

  get localSearchOnly(): boolean {
    return this.enrichSetupService.getCurrentReadiness().localSearchOnly === true;
  }

  readonly breadcrumbs = [
    {label: 'Shine Enrich', route: '/enrich/setup'},
    {label: 'Company data', route: '/enrich/companies'},
    {label: 'Search'}
  ];

  // Helper method to format numeric comparison for display
  formatNumericComparison(field: string, value: any): string {
    if (!value) return '';

    const fieldLabel = this.getFieldLabel(field);

    if (typeof value === 'number') {
      return `${fieldLabel}: ${this.formatNumber(value)}`;
    }

    if (typeof value === 'object') {
      if (value.between) {
        return `${fieldLabel}: ${this.formatNumber(value.between[0])} - ${this.formatNumber(value.between[1])}`;
      } else if (value.gt !== undefined) {
        return `${fieldLabel}: > ${this.formatNumber(value.gt)}`;
      } else if (value.lt !== undefined) {
        return `${fieldLabel}: < ${this.formatNumber(value.lt)}`;
      } else if (value.gte !== undefined) {
        return `${fieldLabel}: ≥ ${this.formatNumber(value.gte)}`;
      } else if (value.lte !== undefined) {
        return `${fieldLabel}: ≤ ${this.formatNumber(value.lte)}`;
      }
    }

    return `${fieldLabel}: ${value}`;
  }

  // Helper method to get a human-readable field label
  getFieldLabel(field: string): string {
    const labels: {[key: string]: string} = {
      'number_of_employees': 'Number of Employees',
      'latest_annual_revenue': 'Annual Revenue',
      'latest_ebit': 'EBIT',
      'latest_sum_equity': 'Sum Equity',
      'company_id': 'Company ID',
      'city': 'City',
      'municipality': 'Municipality',
      'postal_code': 'Postal Code',
      'region': 'Region',
      'organization_type': 'Organization Type',
      'accounting_year': 'Accounting Year',
      'vat_registered': 'VAT Registered',
      'in_company_register': 'In Company Register',
      'in_foundation_register': 'In Foundation Register',
      'in_voluntary_register': 'In Voluntary Register',
      'in_party_register': 'In Party Register',
      'nace_code1': 'NACE Code',
      'number_of_employees_group': 'Employee Group'
    };

    return labels[field] || field;
  }

  // Helper method to format numbers according to locale
  formatNumber(value: number): string {
    return new Intl.NumberFormat().format(value);
  }

  searchForm: FormGroup;
  get registrationFilterCount(): number { return registrationFilterCount(this.searchForm.value); }
  get numericFilterCount(): number { return numericFilterCount(this.searchForm.value, this.numericFields); }
  companies: BrregCompany[] = [];
  totalCount: number = 0;
  hasSearched: boolean = false;
  readonly facets = inject(FilterFacetsService);
  get filterOptions() { return this.facets.options(); }
  regionSelectionChanged = false;
  readonly multiselectFields = ['municipality', 'region', 'organization_type'];
  isLoading: boolean = false;
  isSearching: boolean = false;
  isImporting: boolean = false;
  isLoadingImported: boolean = false;
  error: string | null = null;
  importedError: string | null = null;
  selectedIndex: number = 0; // Default to the first content tab (index 0)
  queryParamName: string = 'tab'; // Name of the query parameter for tab index (legacy: 1=Search, 2=Imported)
  displayedColumns: string[] = ['company_name', 'company_id', 'postal_place', 'employees', 'accounting_year', 'actions'];
  importedDisplayedColumns: string[] = ['name', 'org_number', 'domain', 'last_synced_at', 'actions'];
  importedCompanies: string[] = [];
  importedHubspotCompanies: HubspotCompany[] = [];
  importedOrgNumberProperty: string = 'company_id';
  hasLoadedImportedCompanies: boolean = false;
  importedNextAfter: string | null = null;
  importedTruncation: HubspotImportedCompaniesTruncation | null = null;
  private importedAfter: string | null = null;
  private importedPreviousAfters: Array<string | null> = [];
  private importedClientId: string | null = null;
  private importedLoadGeneration: number = 0;

  // Numeric fields that support comparison operators
  numericFields: string[] = [
    'number_of_employees',
    'latest_annual_revenue',
    'latest_ebit',
    'latest_sum_equity'
  ];

  // Standard filter fields
  standardFilterFields: string[] = [
    'company_id',
    'city',
    'municipality',
    'postal_code',
    'region',
    'organization_type',
    'accounting_year',
    'vat_registered',
    'in_company_register',
    'in_foundation_register',
    'in_voluntary_register',
    'in_party_register'
  ];

  // Comparison operators
  comparisonOperators = [
    { value: 'eq', label: 'Equal to (=)' },
    { value: 'gt', label: 'Greater than (>)' },
    { value: 'lt', label: 'Less than (<)' },
    { value: 'gte', label: 'Greater than or equal to (>=)' },
    { value: 'lte', label: 'Less than or equal to (<=)' },
    { value: 'between', label: 'Between' }
  ];

    constructor() {
    // Initialize the form with basic fields
    this.searchForm = this.fb.group({
      companyName: [''],
      orgNumber: [''],
      postalCode: [''],
      naceCode: [[]],
      employeeGroup: [[]]
    });

    // Add numeric fields with comparison operators
    this.numericFields.forEach(field => {
      this.searchForm.addControl(field, this.fb.group({
        operator: ['eq'], // Default to equals
        value: [''],
        min: [''],
        max: ['']
      }));
    });

    // Add standard filter fields
    this.standardFilterFields.forEach(field => {
      // Skip fields that are already in the form
      if (!this.searchForm.contains(field) && 
          field !== 'postal_code' && // Skip postal_code as it's already in the form as postalCode
          field !== 'company_id') {  // Skip company_id as it's already in the form as orgNumber
        this.searchForm.addControl(field, this.fb.control(this.multiselectFields.includes(field) ? [] : ''));
      }
    });
  }

  ngOnInit(): void {
    if (this.localSearchOnly) this.displayedColumns = this.displayedColumns.filter(column => column !== 'actions');
    // Initialize the selected tab from URL query parameters
    const indexParam = this.route.snapshot.queryParamMap.get(this.queryParamName);
    this.selectedIndex = this.parseTabIndex(indexParam);

    // Subscribe to query parameter changes
    this.route.queryParams.subscribe(params => {
      this.selectedIndex = this.parseTabIndex(params[this.queryParamName] ?? '');
      if (this.selectedIndex === 1 && this.clientService.client?.id) {
        this.loadImportedCompanies();
      }
    });

      // Use ClientLoadingService to ensure a client is selected
      this.isLoading = true;
      this.error = null;

      this.clientLoadingService.ensureClientSelected().subscribe({
        next: () => {
              // Client is loaded and selected, proceed with loading filter options
              this.loadFilterOptions();
              if (this.selectedIndex === 1) {
                this.loadImportedCompanies();
              }
          },
          error: (err) => {
              this.enrichDiagnostics.reportError('enrich.companies.client-selection', err);
              this.error = 'No client selected. Please select a client from the dropdown.';
              this.isLoading = false;
          }
      });

  }

  private parseTabIndex(tabValue: unknown): number {
    if (this.localSearchOnly) return 0;
    if (typeof tabValue !== 'string' || !/^(1|2)$/.test(tabValue)) {
      return 0;
    }

    return Number(tabValue) - 1;
  }

  onTabChange(event: MatTabChangeEvent): void {
    if (this.localSearchOnly && event.index !== 0) return;
    this.selectedIndex = event.index;
    if (event.index === 1) {
      this.loadImportedCompanies();
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [this.queryParamName]: event.index + 1 },
      queryParamsHandling: 'merge'
    });
  }

  loadFilterOptions(): void {
    const clientId = this.clientService.client?.id;
    if (!clientId) return;
    this.facets.connect(this.searchForm.valueChanges, () => this.getSearchParams(), clientId, options => {
      if (!this.regionSelectionChanged) return;
      this.regionSelectionChanged = false;
      if (!selectionValues(this.searchForm.get('region')?.value).length) return;
      const control = this.searchForm.get('municipality')!;
      const selected = selectionValues(control.value);
      const allowed = new Set(options.municipality.map(option => option.value));
      const remaining = selected.filter(value => allowed.has(value));
      if (remaining.length !== selected.length) {
        control.setValue(remaining);
        this.toastService.showInfo('Municipality selections were adjusted to the selected regions.', 4000);
      }
    });
    this.isLoading = false;
  }

  searchCompanies(): void {
    if (this.searchForm.invalid) {
      return;
    }

    this.isSearching = true;
    this.error = null;
    this.companies = [];
    this.totalCount = 0;
    this.hasSearched = false;

    const clientId = this.clientService.client?.id;
    if (!clientId) {
      this.error = 'No client selected';
      this.isSearching = false;
      return;
    }

    const searchParams = this.getSearchParams();

    this.companyService.searchCompanies(searchParams, clientId).pipe(
      catchError(() => {
        this.error = 'Failed to search companies';
        return of(null);
      }),
      finalize(() => {
        this.isSearching = false;
      })
    ).subscribe(response => {
      if (!response) {
        return;
      }
      this.companies = response.companies;
      this.totalCount = response.totalCount;
      this.hasSearched = true;
      if (response.totalCount === 0) {
        this.toastService.showInfo('No companies found matching your search criteria', 3500);
      }
    });
  }

  getSearchParams(): any {
    const formValue = this.searchForm.value;
    const params: any = {};

    // Handle basic fields
    if (formValue.companyName) {
      params.company_name = formValue.companyName;
    }
    if (formValue.orgNumber) {
      params.company_id = formValue.orgNumber;
    }
    if (formValue.postalCode) {
      params.postal_code = formValue.postalCode;
    }
    if (selectionValues(formValue.naceCode).length) {
      params.nace_code1 = selectionValues(formValue.naceCode);
    }
    if (selectionValues(formValue.employeeGroup).length) {
      params.number_of_employees_group = selectionValues(formValue.employeeGroup);
    }

    // Handle standard filter fields
    this.standardFilterFields.forEach(field => {
      // Skip fields that are handled above
      if (field !== 'postal_code' && field !== 'company_id' && formValue[field]) {
        if (this.multiselectFields.includes(field)) {
          const values = selectionValues(formValue[field]);
          if (values.length) params[field] = values;
        } else {
          params[field] = formValue[field];
        }
      }
    });

    // Handle numeric fields with comparison operators
    this.numericFields.forEach(field => {
      const fieldData = formValue[field];
      if (fieldData && typeof fieldData === 'object') {
        const { operator, value, min, max } = fieldData;

        // Convert to the format expected by the backend
        let comparisonValue: any;

        switch (operator) {
          case 'eq':
            // For equals, just use the value directly
            comparisonValue = hasFilterValue(value) ? Number(value) : undefined;
            break;
          case 'gt':
            comparisonValue = hasFilterValue(value) ? { gt: Number(value) } : undefined;
            break;
          case 'lt':
            comparisonValue = hasFilterValue(value) ? { lt: Number(value) } : undefined;
            break;
          case 'gte':
            comparisonValue = hasFilterValue(value) ? { gte: Number(value) } : undefined;
            break;
          case 'lte':
            comparisonValue = hasFilterValue(value) ? { lte: Number(value) } : undefined;
            break;
          case 'between':
            // Validate that min is less than max
            if (hasFilterValue(min) && hasFilterValue(max) && Number(min) > Number(max)) {
              this.error = `For field "${field}", minimum value must be less than maximum value`;
              // Don't add this field to params
              return;
            }
            comparisonValue = (hasFilterValue(min) && hasFilterValue(max)) ? { between: [Number(min), Number(max)] } : undefined;
            break;
          default:
            comparisonValue = undefined;
        }

        // Only add the parameter if it has a value
        if (comparisonValue !== undefined) {
          params[field] = comparisonValue;
        }
      }
    });

    return params;
  }

  importCompany(company: BrregCompany): void {
    if (this.localSearchOnly) return;
    const clientId = this.clientService.client?.id;
    if (!clientId) {
      this.error = 'No client selected';
      return;
    }

    this.isImporting = true;
    this.error = null;
    this.companyService.importCompany(company.company_id, clientId).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error?.status === 409) {
          this.error = error.error?.error || `${company.company_name} already exists in HubSpot`;
          this.markCompanyImported(company.company_id);
        } else {
          this.error = `Failed to import company ${company.company_name}`;
        }
        return of(null);
      }),
      finalize(() => {
        this.isImporting = false;
      })
    ).subscribe(response => {
      if (response) {
        this.markCompanyImported(company.company_id);
        this.toastService.showSuccess('Company imported successfully', 4000);
        if (this.hasLoadedImportedCompanies || this.selectedIndex === 1) {
          this.loadImportedCompanies(true);
        }
      }
    });
  }

  viewCompany(company: BrregCompany): void {
    if (!company?.company_id) {
      return;
    }

    this.router.navigate(['/enrich/companies', company.company_id], {
      queryParamsHandling: 'preserve',
      state: {company}
    });
  }

  importSelectedCompanies(): void {
    // Implementation for importing multiple selected companies
    // This would be implemented if we add checkboxes to select multiple companies
  }

  loadImportedCompanies(force: boolean = false): void {
    if (this.localSearchOnly) return;
    const clientId = this.clientService.client?.id;
    if (!clientId) {
      this.importedError = 'No client selected';
      return;
    }

    const clientChanged = this.importedClientId !== null && this.importedClientId !== clientId;
    if (clientChanged) {
      this.importedHubspotCompanies = [];
      this.importedCompanies = [];
      this.hasLoadedImportedCompanies = false;
      this.importedAfter = null;
      this.importedNextAfter = null;
      this.importedPreviousAfters = [];
      this.importedTruncation = null;
    }

    if ((this.isLoadingImported && !clientChanged) || (this.hasLoadedImportedCompanies && !force && !clientChanged)) {
      return;
    }

    this.loadImportedCompaniesPage(clientId, null, []);
  }

  loadNextImportedCompaniesPage(): void {
    if (!this.importedClientId || !this.importedNextAfter || this.isLoadingImported) {
      return;
    }

    this.loadImportedCompaniesPage(
      this.importedClientId,
      this.importedNextAfter,
      [...this.importedPreviousAfters, this.importedAfter]
    );
  }

  loadPreviousImportedCompaniesPage(): void {
    if (!this.importedClientId || this.importedPreviousAfters.length === 0 || this.isLoadingImported) {
      return;
    }

    const previousAfters = [...this.importedPreviousAfters];
    const after = previousAfters.pop() ?? null;
    this.loadImportedCompaniesPage(this.importedClientId, after, previousAfters);
  }

  hasPreviousImportedCompaniesPage(): boolean {
    return this.importedPreviousAfters.length > 0;
  }

  getImportedCompaniesPageNumber(): number {
    return this.importedPreviousAfters.length + 1;
  }

  private loadImportedCompaniesPage(
    clientId: string,
    after: string | null,
    previousAfters: Array<string | null>
  ): void {
    const loadGeneration = ++this.importedLoadGeneration;
    this.importedClientId = clientId;

    this.isLoadingImported = true;
    this.importedError = null;

    forkJoin({
      settings: this.hubspotService.getSettings(clientId).pipe(
        catchError(() => of(null as EnrichSettings | null))
      ),
      page: this.hubspotService.getImportedCompaniesPage(clientId, {limit: 100, after})
    }).pipe(
      catchError(() => {
        if (loadGeneration === this.importedLoadGeneration && this.clientService.client?.id === clientId) {
          this.importedError = 'Failed to load imported companies';
        }
        return of(null);
      }),
      finalize(() => {
        if (loadGeneration === this.importedLoadGeneration) {
          this.isLoadingImported = false;
        }
      })
    ).subscribe(result => {
      if (
        !result ||
        loadGeneration !== this.importedLoadGeneration ||
        this.clientService.client?.id !== clientId
      ) {
        return;
      }

      const {settings, page} = result;
      this.importedOrgNumberProperty = settings?.crmOrgNumberProperty || 'company_id';
      this.importedHubspotCompanies = page.items;
      this.importedAfter = page.after;
      this.importedNextAfter = page.hasMore ? page.nextAfter : null;
      this.importedPreviousAfters = previousAfters;
      this.importedTruncation = page.truncation;
      this.importedHubspotCompanies
        .map(company => this.getImportedCompanyOrgNumber(company))
        .filter((orgNumber): orgNumber is string => !!orgNumber)
        .forEach(orgNumber => this.markCompanyImported(orgNumber));
      this.hasLoadedImportedCompanies = true;
    });
  }

  getImportedCompanyName(company: HubspotCompany): string {
    return this.normalizePropertyValue(company.properties?.['name']) || 'Unnamed company';
  }

  getImportedCompanyOrgNumber(company: HubspotCompany): string | null {
    const properties = company.properties || {};
    const candidates = [
      properties[this.importedOrgNumberProperty],
      properties['company_id'],
      properties['org_number'],
      properties['organization_number'],
      properties['organisasjonsnummer']
    ];

    for (const candidate of candidates) {
      const value = this.normalizePropertyValue(candidate);
      if (value) {
        return value;
      }
    }

    return null;
  }

  getImportedCompanyDomain(company: HubspotCompany): string {
    return this.normalizePropertyValue(company.properties?.['domain']) || '-';
  }

  getImportedCompanyLastSyncedAt(company: HubspotCompany): string {
    const rawValue =
      this.normalizePropertyValue(company.properties?.['shine_enrich_last_synced_at']) ||
      this.normalizePropertyValue(company.properties?.['shine_enrich_first_synced_at']) ||
      company.updatedAt ||
      company.createdAt;

    if (!rawValue) {
      return '-';
    }

    const date = new Date(rawValue);
    return isNaN(date.getTime()) ? rawValue : date.toLocaleDateString();
  }

  viewImportedCompany(company: HubspotCompany): void {
    const orgNumber = this.getImportedCompanyOrgNumber(company);
    if (!orgNumber) {
      return;
    }

    this.router.navigate(['/enrich/companies', orgNumber], {
      queryParamsHandling: 'preserve'
    });
  }

  resetForm(): void {
    // Reset basic fields
    this.searchForm.patchValue({
      companyName: '',
      orgNumber: '',
      postalCode: '',
      naceCode: [],
      employeeGroup: []
    });

    // Reset standard filter fields
    this.standardFilterFields.forEach(field => {
      if (this.searchForm.contains(field) && 
          field !== 'postal_code' && // Skip postal_code as it's already reset above
          field !== 'company_id') {  // Skip company_id as it's already reset above
        this.searchForm.get(field)?.setValue(this.multiselectFields.includes(field) ? [] : '');
      }
    });

    // Reset numeric fields
    this.numericFields.forEach(field => {
      const numericGroup = this.searchForm.get(field) as FormGroup;
      if (numericGroup) {
        numericGroup.patchValue({
          operator: 'eq',
          value: '',
          min: '',
          max: ''
        });
      }
    });

    this.companies = [];
    this.totalCount = 0;
    this.hasSearched = false;
    this.error = null;
  }

  isCompanyImported(company_id: string) {
    return this.importedCompanies.indexOf(company_id) !== -1;
  }

  private isImportedHubspotCompany(company: HubspotCompany): boolean {
    return this.isTruthyProperty(company.properties?.['shine_enrich_synced']);
  }

  private isTruthyProperty(value: unknown): boolean {
    return value === true || value === 1 || value === 'true' || value === '1';
  }

  private markCompanyImported(companyId: string): void {
    if (companyId && !this.isCompanyImported(companyId)) {
      this.importedCompanies.push(companyId);
    }
  }

  private normalizePropertyValue(value: unknown): string | null {
    if (value === undefined || value === null) {
      return null;
    }

    const normalized = String(value).trim();
    return normalized || null;
  }
}
